import { HttpResponse } from '@angular/common/http';
import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfiguratorProviderService, DatastoreDto, ProviderDTO} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {v4 as uuidv4} from 'uuid';
import {TranslateService} from '@ngx-translate/core';
import { FilterMenuByPermissionsPipe } from '../../../pipes/filter-menu-by-permissions.pipe';
import {Router} from '@angular/router';

// Extended interface to add the index property for table dataKey
interface ProviderWithIndex extends ProviderDTO {
    index: string;
}

@Component({
    selector: 'dmp-provider',
    templateUrl: './provider.component.html',
    styleUrls: ['./provider.component.scss'],
    providers: [ConfirmationService, MessageService],
    standalone: false
})
export class ProviderComponent implements OnInit {
    breadcrumb = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.providers')}
    ];
    description = this.translate.instant('provider.list.description');

    @ViewChild('providerFileInput')
    providerFileInput: ElementRef;

    private actionItemsByRowKey: Record<string, MenuItem[]> = {};

    providers: ProviderWithIndex[] = [];
    selectedProviders: ProviderWithIndex[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];


    datastore: DatastoreDto;

    // Modale d'ajout / d'édition : `editedProvider` vaut `null` en création
    providerDialogVisible = false;
    editedProvider: ProviderWithIndex = null;
    isSavingProvider = false;
    providerForm: UntypedFormGroup = this.formBuilder.group({
        code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(10)]],
        // `\S` : un nom fait uniquement d'espaces est refusé, comme le faisait la validation en ligne
        name: ['', [Validators.required, Validators.pattern(/\S/)]],
        description: ['']
    });

    get hasActions(): boolean {
        return true;
    }

    constructor(
        protected providerService: ConfiguratorProviderService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private formBuilder: UntypedFormBuilder,
        private readonly translate: TranslateService,
        private filterMenuByPermissionsPipe: FilterMenuByPermissionsPipe,
        private router: Router
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.data-center.label')},
            {label: this.translate.instant('menu.data-center.providers')}
        ];
        this.description = this.translate.instant('provider.list.description');
    }

    ngOnInit(): void {
        this.initExportMenu();
        const savedName = this.router.getCurrentNavigation()?.extras.state.savedProviderName;
        if (savedName) {
            this.messageService.add({severity: 'success', summary: 'Fournisseur enregistré', detail: savedName});
        }
    }

    loadAll() {
        return new Promise<void>((resolve) =>
            this.providerService
                .getAllProvidersUsingGET(this.datastore.code, 'response')
                .subscribe((res: HttpResponse<ProviderDTO[]>) => {
                    this.providers =
                        res.body.map((el) => ({
                            ...el,
                            index: uuidv4(),
                        } as ProviderWithIndex)) ?? [];
                    this.actionItemsByRowKey = {};
                    this.updateExportMenuState();
                    resolve();
                })
        );
    }

    productNames(provider: ProviderDTO): string {
        const labels = {flight: 'Vol', hotel: 'Hôtel', car: 'Voiture', parking: 'Parking'};
        return provider.products?.map(product => labels[product] || product).join(', ') || 'À configurer';
    }

    startEdit(index: number) {
        if (this.datastore && this.providers[index]?.id) {
            this.router.navigate(['/h/settings/provider/edit', this.datastore.code, this.providers[index].id]);
        }
    }

    create() {
        if (!this.datastore) { return; }
        this.router.navigate(['/h/settings/provider/create', this.datastore.code]);
    }

    remove(index: number) {
        this.confirmationService.confirm({
            // target: event.target,
            message: 'Êtes vous sur de vouloir supprimer ?',
            icon: 'fas fa-exclamation-triangle',
            accept: () => {
                this.providerService
                    .deleteProviderUsingDELETE(this.datastore.code, this.providers[index].id)
                    .subscribe(
                        async () => {
                            const el = {...this.providers[index]};
                            await this.loadAll();
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Suppression réalisé avec succès',
                                detail: `Le fournisseur ${el.name} a bien été supprimé.`,
                            });
                        },
                        (error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary:
                                    'Une erreur est survenu à la suppression',
                                detail: 'Impossible de supprimer le fournisseur',
                            });
                        }
                    );
            },
        });
    }

    async datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        if (datastore) {
            await this.loadAll();
        } else {
            this.providers = [];
        }
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedProviders(),
                disabled: !this.selectedProviders || this.selectedProviders.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllProviders(),
                disabled: !this.providers || this.providers.length === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.providerFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.providerService.importProviderJson(this.datastore.code, jsonContent).subscribe(
                (provider: ProviderDTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('provider.list.importSuccessDetail', {name: provider.name})
                    });
                    this.loadAll();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.providerService.importProvidersZip(this.datastore.code, file).subscribe(
            (providers: ProviderDTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('provider.list.importSuccessMultipleDetail', {count: providers.length})
                });
                this.loadAll();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte un seul fournisseur en JSON
     */
    exportProviderJson(provider: ProviderWithIndex) {
        this.providerService.exportProviderJson(this.datastore.code, provider.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `provider-${provider.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('provider.list.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs fournisseurs en ZIP
     */
    exportSelectedProviders() {
        if (!this.selectedProviders || this.selectedProviders.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('provider.list.noProviderSelectedDetail')
            });
            return;
        }

        const ids = this.selectedProviders.map(p => p.id);
        this.providerService.exportProvidersZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `providers-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('provider.list.exportSuccessMultipleDetail', {count: this.selectedProviders.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte tous les fournisseurs en ZIP
     */
    exportAllProviders() {
        if (!this.providers || this.providers.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('provider.list.noProviderToExportDetail')
            });
            return;
        }

        const ids = this.providers.map(p => p.id);
        this.providerService.exportProvidersZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `providers-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('provider.list.exportSuccessMultipleDetail', {count: this.providers.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedProviders || this.selectedProviders.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.providers || this.providers.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }

    // Les menus sont mis en cache par ligne : le template rappelle cette méthode à chaque détection
    // de changement, et un nouveau tableau ferait recalculer le filtre de permissions et redessiner
    // le menu. Les commandes reçoivent le fournisseur lui-même, pas sa position dans la liste.
    getItemsListAction(provider: ProviderWithIndex, index: number, editing: boolean): MenuItem[] {
        const rowKey = provider.index;
        const cached = this.actionItemsByRowKey[rowKey];
        if (cached) {
            return cached;
        }

        const items: MenuItem[] = [
                {
                    label: this.translate.instant('subscriptions.edit'),
                    icon: 'fas fa-pen',
                    permission: 'PROVIDERS.edit',
                    command: () => this.startEdit(index),
                },
                {
                    label: this.translate.instant('subscriptions.delete'),
                    icon: 'fas fa-trash',
                    permission: 'PROVIDERS.delete',
                    command: () => this.remove(index),
                },
                {
                    separator: true
                },
                {
                    label: this.translate.instant('subscriptions.exportJson'),
                    icon: 'fas fa-download',
                    permission: 'PROVIDERS.read',
                    command: () => {
                        if (provider?.id) {
                            this.exportProviderJson(provider);
                        }
                    },
                },
            ];

        this.actionItemsByRowKey[rowKey] = items;
        return items;
    }
}

